import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Lock, ShieldAlert, AlertCircle, CheckCircle2, Search, X, Book } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { SiteHeader } from '@/components/site-header';
import { EmptyState as EmptyStateComponent } from '@/components/ui/empty-state';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useAuth } from '@/context/AuthContext';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { KnowledgeBaseCard } from './components/knowledge-base-card';
import { KnowledgeBaseCardSkeleton } from './components/knowledge-base-card-skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getAllKnowledgeBases, deleteKnowledgeBase } from '@/api/knowledge-base/knowledge-base.api';
import type { KnowledgeBase } from '@/api/knowledge-base/knowledge-base.types';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            duration: 0.3
        }
    }
};

const searchTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'full_text', label: 'Full Text' },
    { value: 'semantic', label: 'Semantic' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'bm25', label: 'BM25' },
];

const KnowledgeBasePage = () => {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTypeFilter, setSearchTypeFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const { error, success } = useEnhancedToast();
    const navigate = useNavigate();
    const canEdit = user?.permissions?.knowledge_base === 'EDITOR';
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        id: string;
        name: string;
    } | null>(null);

    // Filter knowledge bases based on search query and type
    const filteredKnowledgeBases = useMemo(() => {
        let filtered = knowledgeBases;

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(kb =>
                (kb.name || '').toLowerCase().includes(query) ||
                kb.id.toLowerCase().includes(query)
            );
        }

        // Filter by search type
        if (searchTypeFilter !== 'all') {
            filtered = filtered.filter(kb =>
                kb.searchType === searchTypeFilter
            );
        }

        return filtered;
    }, [knowledgeBases, searchQuery, searchTypeFilter]);

    useEffect(() => {
        fetchKnowledgeBases();
    }, []);

    const fetchKnowledgeBases = async () => {
        try {
            const { data, error: fetchError } = await getAllKnowledgeBases();
            if (fetchError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Load Knowledge Bases",
                    description: fetchError,
                    duration: 4000,
                });
                return;
            }
            if (data) {
                const sortedKnowledgeBases = [...data].sort((a, b) =>
                    (b.name || '').localeCompare(a.name || '')
                );
                setKnowledgeBases(sortedKnowledgeBases);
            }
        } catch (err: any) {
            error({
                icon: AlertCircle,
                title: "Failed to Load Knowledge Bases",
                description: "Could not fetch knowledge bases. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (id: string, name: string) => {
        setDeleteConfirmation({ id, name });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;

        try {
            const { error: deleteError } = await deleteKnowledgeBase(deleteConfirmation.id);
            if (deleteError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Delete Knowledge Base",
                    description: deleteError,
                    duration: 4000,
                });
                return;
            }
            setKnowledgeBases(prev => prev.filter(kb => kb.id !== deleteConfirmation.id));
            success({
                icon: CheckCircle2,
                title: "Knowledge Base Deleted",
                description: `Successfully deleted ${deleteConfirmation.name}`,
                duration: 3000,
            });
        } catch (err: any) {
            error({
                icon: AlertCircle,
                title: "Failed to Delete Knowledge Base",
                description: "An error occurred while deleting the knowledge base",
                duration: 4000,
            });
        } finally {
            setDeleteConfirmation(null);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Knowledge Base"
                subtitle="Manage your knowledge bases and vector stores"
            >
                {knowledgeBases.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search by name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[200px] lg:w-[300px] pl-9 pr-9"
                            />
                            {searchQuery && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>

                        {/* Search Type Filter */}
                        <Combobox
                            options={searchTypeOptions}
                            value={searchTypeFilter}
                            onValueChange={setSearchTypeFilter}
                            placeholder="Filter by type..."
                            className="w-[160px]"
                        />

                        {/* Create Button */}
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <Button
                                            onClick={() => canEdit && navigate('new')}
                                            size="sm"
                                            className="gap-2"
                                            disabled={!canEdit}
                                        >
                                            <Plus className="h-4 w-4" />
                                            New Knowledge Base
                                            {!canEdit && <Lock className="h-3.5 w-3.5 opacity-70" />}
                                        </Button>
                                    </div>
                                </TooltipTrigger>
                                {!canEdit && (
                                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                                        <p>You need editor permissions to create knowledge bases. Contact your administrator for access.</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, index) => (
                                <KnowledgeBaseCardSkeleton key={index} />
                            ))}
                        </div>
                    </motion.div>
                ) : knowledgeBases.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        {filteredKnowledgeBases.length > 0 ? (
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            >
                                {filteredKnowledgeBases.map((kb) => (
                                    <KnowledgeBaseCard
                                        key={kb.id}
                                        knowledgeBase={kb}
                                        onDelete={() => handleDeleteClick(kb.id, kb.name || 'Unnamed')}
                                        onEdit={() => navigate(`${kb.id}`)}
                                        variants={itemVariants}
                                    />
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className="flex items-center justify-center"
                                style={{ height: 'calc(100vh - 200px)' }}
                            >
                                <div className="text-center">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                        <Search className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">No Matching Knowledge Bases</h3>
                                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                        We couldn't find any knowledge bases matching your search query.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSearchTypeFilter('all');
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyStateComponent
                                icon={Book}
                                title="No Knowledge Bases Yet"
                                description="Create knowledge bases to organize and manage your content efficiently."
                                action={{
                                    label: "Create Knowledge Base",
                                    onClick: () => navigate('new'),
                                    icon: Plus,
                                    disabled: !canEdit,
                                    disabledReason: "You need editor permissions to create knowledge bases. Contact your administrator for access.",
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </div>

            <AlertDialog open={!!deleteConfirmation} onOpenChange={() => setDeleteConfirmation(null)}>
                <AlertDialogContent className="gap-3">
                    <AlertDialogHeader className="space-y-1">
                        <AlertDialogTitle className="text-base">Delete Knowledge Base</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteConfirmation?.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction size="sm" onClick={handleDeleteConfirm}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default KnowledgeBasePage;
